import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { ActivityFilters } from "@/components/super-admin/ActivityFilters";
import { ActivityStatsCards } from "@/components/super-admin/ActivityStatsCards";
import { ActivityTable } from "@/components/super-admin/ActivityTable";
import { ActivityDetailsModal } from "@/components/super-admin/ActivityDetailsModal";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ActivityFilters {
  gymIds: string[];
  actions: string[];
  startDate?: Date;
  endDate?: Date;
  search: string;
}

interface Activity {
  id: string;
  user_id: string;
  gym_id: string;
  action: string;
  target: string;
  target_type?: string;
  target_id?: string;
  target_role?: string;
  full_name?: string;
  created_at: string;
  class_name?: string;
  booking_date?: string;
  class_time?: string;
  booking_class_id?: string;
}

export default function SuperAdminActivity() {
  const [filters, setFilters] = useState<ActivityFilters>({
    gymIds: [],
    actions: [],
    search: "",
  });
  const [page, setPage] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation(["superadmin2", "common"]);

  const pageSize = 50;

  // Fetch gyms for filter
  const { data: gymsData } = useQuery({
    queryKey: ["super-admin-gyms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gyms")
        .select("id, name, city")
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch activities
  const {
    data: activitiesData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["super-admin-activities", filters, page],
    queryFn: async () => {
      const payload = {
        gymIds: filters.gymIds,
        actions: filters.actions,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        search: filters.search,
        page,
        pageSize,
      };

      const { data, error } = await supabase.functions.invoke(
        "get-super-admin-activity",
        { body: payload }
      );

      if (error) throw error;
      return data;
    },
  });

  const handleFilterChange = (newFilters: ActivityFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleViewDetails = (activity: Activity) => {
    setSelectedActivity(activity);
    setDetailsModalOpen(true);
  };

  const handleRefresh = async () => {
    await refetch();
    toast.success(t("activity.refreshed"));
  };

  const totalPages = Math.ceil((activitiesData?.total || 0) / pageSize);
  const selectedGym = selectedActivity
    ? gymsData?.find((g) => g.id === selectedActivity.gym_id)
    : undefined;

  return (
    <SuperAdminLayout>
      <div className="space-y-3 md:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
              {isMobile ? t("activity.title") : t("activity.titleFull")}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              {t("activity.subtitle")}
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={isFetching}
            variant="outline"
            size={isMobile ? "icon" : "default"}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Stats */}
        {activitiesData?.stats && (
          <ActivityStatsCards stats={activitiesData.stats} />
        )}

        {/* Filters */}
        <ActivityFilters
          gyms={gymsData || []}
          onFilterChange={handleFilterChange}
        />

        {/* Results Info */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <p className="text-xs md:text-sm text-muted-foreground">
            {isLoading ? (
              t("activity.loading")
            ) : (
              <>
                {t("activity.activitiesFound", { count: activitiesData?.total || 0 })}
                {filters.gymIds.length > 0 && ` • ${t("activity.tenantsFiltered", { count: filters.gymIds.length })}`}
                {filters.actions.length > 0 && ` • ${t("activity.actionsFiltered", { count: filters.actions.length })}`}
              </>
            )}
          </p>
        </div>

        {/* Table */}
        <ActivityTable
          activities={activitiesData?.activities || []}
          gyms={gymsData || []}
          onViewDetails={handleViewDetails}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent className="gap-1">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className={cn(
                      "h-9 w-9 p-0",
                      page === 0 && "pointer-events-none opacity-50"
                    )}
                  />
                </PaginationItem>
                
                {Array.from({ length: Math.min(isMobile ? 3 : 5, totalPages) }, (_, i) => {
                  const offset = isMobile ? 1 : 2;
                  const pageNum = Math.max(0, Math.min(page - offset, totalPages - (isMobile ? 3 : 5))) + i;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={pageNum === page}
                        className="cursor-pointer h-9 w-9 p-0 text-xs md:text-sm"
                      >
                        {pageNum + 1}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    className={cn(
                      "h-9 w-9 p-0",
                      page >= totalPages - 1 && "pointer-events-none opacity-50"
                    )}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        {/* Details Modal */}
        <ActivityDetailsModal
          activity={selectedActivity}
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          gym={selectedGym}
        />
      </div>
    </SuperAdminLayout>
  );
}
